/**
 * Frontend metadata for Profile Photo presets.
 *
 * Mirrors the `id`, `gender`, and `nameKey` of each preset in
 * server/src/config/profilePhotoPresets.ts. The server is the source
 * of truth for `systemPrompt` (kept server-only). The frontend owns
 * `previewImage` since it's a static asset shown in the picker.
 *
 * Add `nameKey` strings under `profilePhoto.presets.*` in
 * src/lib/i18n/translations.ts.
 */

export type ProfilePhotoGender = "male" | "female";

export interface ProfilePhotoPreset {
  id: string;
  gender: ProfilePhotoGender;
  nameKey: string;
  previewImage: string;
  /** Custom preset: rendered as a "build your own" card (no avatar). Selecting
   *  it opens the modal where the user supplies clothing + background. */
  isCustom?: boolean;
}

const CDN_BASE = "https://cdn.todowith.ai/generated/2ae2c1db-53d0-4920-9cf4-a6b5a96db17b";

export const PROFILE_PHOTO_PRESETS: ProfilePhotoPreset[] = [
  // ── Male ──
  { id: "male-custom",           gender: "male",   nameKey: "customStyle",         previewImage: "", isCustom: true },
  { id: "male-corporate-suit",   gender: "male",   nameKey: "maleCorporateSuit",   previewImage: `${CDN_BASE}/preset-profile-photo-male-corporate-suit.webp` },
  { id: "male-business-casual",  gender: "male",   nameKey: "maleBusinessCasual",  previewImage: `${CDN_BASE}/preset-profile-photo-male-business-casual.webp` },
  { id: "male-tech-startup",     gender: "male",   nameKey: "maleTechStartup",     previewImage: `${CDN_BASE}/preset-profile-photo-male-tech-startup.webp` },
  { id: "male-classic-portrait", gender: "male",   nameKey: "maleClassicPortrait", previewImage: `${CDN_BASE}/preset-profile-photo-male-classic-portrait.webp` },
  // ── Female ──
  { id: "female-custom",           gender: "female", nameKey: "customStyle",           previewImage: "", isCustom: true },
  { id: "female-corporate-suit",   gender: "female", nameKey: "femaleCorporateSuit",   previewImage: `${CDN_BASE}/preset-profile-photo-female-corporate-suit.webp` },
  { id: "female-business-casual",  gender: "female", nameKey: "femaleBusinessCasual",  previewImage: `${CDN_BASE}/preset-profile-photo-female-business-casual.webp` },
  { id: "female-tech-startup",     gender: "female", nameKey: "femaleTechStartup",     previewImage: `${CDN_BASE}/preset-profile-photo-female-tech-startup.webp` },
  { id: "female-classic-portrait", gender: "female", nameKey: "femaleClassicPortrait", previewImage: `${CDN_BASE}/preset-profile-photo-female-classic-portrait.webp` },
];

/** Example before/after pair shown in the hero when the user hasn't uploaded yet. */
export const PROFILE_PHOTO_EXAMPLE_BEFORE = `${CDN_BASE}/hero-profile-photo-before.webp`;
export const PROFILE_PHOTO_EXAMPLE_AFTER = `${CDN_BASE}/hero-profile-photo-after.webp`;

/**
 * Reference photo shown inside the upload-guide popup (the "how to shoot
 * yourself" example) — the female "classic portrait" preset: a clear,
 * face-forward, close-up shot. Swap to any other female preview URL to change.
 */
export const PROFILE_PHOTO_GUIDE_IMAGE = `${CDN_BASE}/preset-profile-photo-female-classic-portrait.webp`;

export const PROFILE_PHOTO_MIN_UPLOADS = 2;
export const PROFILE_PHOTO_MAX_UPLOADS = 3;

/** Max length of each custom-style field (background / clothing). Must match
 *  the server Zod guard in `generateProfilePhotoRequestSchema`. */
export const PROFILE_PHOTO_CUSTOM_FIELD_MAX = 200;

// Image-model selection was removed: one click generates one image per model
// in `PROFILE_PHOTO_MODEL_SET` (see @shared/schemas/api). Credit cost is read
// from the canonical `credit_costs` table via
// `useCreditCosts().getImageGenerateCost(resolution, modelId)` summed across
// that set — the same source the server charges from. No bundle / markup.
